import { useState } from "react";
import { Label } from "../ui/label";
import { Trash2, UploadCloud, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

interface LogoUploaderProps {
  label?: string;
  logoUrl?: string;
  onChange: (url: string) => void;
  onRemove: () => void;
}

async function uploadFileHelper(
  file: File,
  setUploading: (u: boolean) => void,
  onChange: (url: string) => void
) {
  setUploading(true);
  try {
    const token = localStorage.getItem("portfolio_admin_token");
    const res = await fetch(`/api/admin/upload?filename=${encodeURIComponent(file.name)}`, {
      method: "POST",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": file.type || "application/octet-stream",
        "X-Filename": encodeURIComponent(file.name),
      },
      body: file,
    });

    const data = (await res.json()) as { success: boolean; url: string; error?: string };
    if (res.ok && data.success) {
      onChange(data.url);
      toast.success("Image uploaded successfully!");
    } else {
      toast.error(data.error || "Upload failed");
    }
  } catch (err: any) {
    toast.error(err.message || "Failed to upload image");
  } finally {
    setUploading(false);
  }
}

export function LogoUploader({ label = "Company/Org Logo", logoUrl, onChange, onRemove }: LogoUploaderProps) {
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo file size cannot exceed 5MB!");
      return;
    }

    uploadFileHelper(file, setUploading, onChange);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (uploading) return;

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed!");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo file size cannot exceed 5MB!");
      return;
    }

    uploadFileHelper(file, setUploading, onChange);
  };

  return (
    <div className="space-y-1.5 text-left">
      <Label>{label}</Label>
      <div className="flex flex-col sm:flex-row gap-4 items-center mt-1">
        {logoUrl ? (
          <div className="relative group rounded-xl overflow-hidden border border-border/80 h-20 w-20 flex items-center justify-center bg-card/40 shrink-0">
            <img src={logoUrl} alt="Logo preview" className="max-h-full max-w-full object-contain p-2" />
            <button
              type="button"
              disabled={uploading}
              onClick={onRemove}
              className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white cursor-pointer disabled:cursor-not-allowed"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="h-20 w-20 rounded-xl border-2 border-dashed border-border/60 flex items-center justify-center text-muted-foreground bg-muted/20 shrink-0">
            <ImageIcon className="h-7 w-7" />
          </div>
        )}
        <div
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`flex-1 w-full border border-dashed border-border/85 rounded-xl p-4 transition-all duration-200 relative flex flex-col items-center justify-center text-center gap-1.5 ${uploading ? "opacity-60 bg-muted/10 cursor-not-allowed" : "hover:border-brand-primary/60 hover:bg-brand-primary/5 cursor-pointer"
            }`}
        >
          {!uploading && (
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="absolute inset-0 opacity-0 cursor-pointer"
            />
          )}
          {uploading ? (
            <>
              <div className="h-6 w-6 border-2 border-brand-primary border-t-transparent animate-spin rounded-full" />
              <p className="text-xs font-semibold text-foreground">Uploading to R2 storage...</p>
            </>
          ) : (
            <>
              <UploadCloud className="h-6 w-6 text-brand-primary" />
              <p className="text-xs font-semibold text-foreground">
                Drag & drop or <span className="text-brand-primary hover:underline">browse</span>
              </p>
              <p className="text-[10px] text-muted-foreground">PNG, JPG, SVG or WEBP up to 5MB</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
